// ============================================================================
// CollectionReportScreen — money taken and refunded (PMS Phase 2 slice 5, P2-14)
// ============================================================================
// Every receipt and refund the Front Office recorded in a payment-date range,
// with its voucher number, mode and booking — and the totals by mode the cashier
// hands over. Refunds are negative. Bill point is Front Office today; the
// restaurant's collections and checkout settlements join with slices 7–8.
// "Recorded by Accounts" rows carry Fairbooks' voucher, not a CRS number.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { useReportList, useReportSummary } from 'hooks/api/useReport';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import type { ReportListRequest, ReportFilter, CollectionRow, CollectionSummary } from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const voucher = (r: CollectionRow) =>
  r.receiptNumber
  || (r.receiptNumberState === 'PENDING' ? 'Number pending'
    : r.receiptNumberState === 'EXTERNAL' || r.origin === 'FAIRBOOKS' ? 'Fairbooks voucher' : '—');

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

const CollectionReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  React.useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const enabled = !!propertyId && !!from && !!to && from <= to;
  React.useEffect(() => { setPage(1); }, [propertyId, from, to]);

  const listRequest: ReportListRequest = useMemo(() => ({ propertyId, from, to, page, pageSize }), [propertyId, from, to, page, pageSize]);
  const summaryFilter: ReportFilter = useMemo(() => ({ propertyId, from, to }), [propertyId, from, to]);

  const { data: rawData, pagination, loading, error, refetch } = useReportList<CollectionRow>(
    (req) => reportService.getCollection(req), listRequest, enabled);
  const data = useMemo(() => rawData.map((r) => ({ ...r, id: r.paymentId as string })), [rawData]);
  const { data: summary, refetch: refetchSummary } = useReportSummary<CollectionSummary>(
    (f) => reportService.getCollectionSummary(f as ReportFilter), summaryFilter, enabled);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  // Export / Print — formatted files built by the API (the Fairbooks way).
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) { notificationService.toast.warning('Select a property and a valid period first.'); return; }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const { blob, disposition } = await reportService.exportCollection(summaryFilter, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, `Collection_${from}_${to}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, summaryFilter, from, to]);
  const handleExport = useCallback(() => { void exportFile('xlsx'); }, [exportFile]);
  const handlePrint = useCallback(() => { void exportFile('pdf'); }, [exportFile]);

  type Row = CollectionRow & { id: string };
  const columns: SyutGridColumn<Row>[] = useMemo(() => [
    { id: 'paymentDate', header: 'Date', accessorKey: 'paymentDate', size: 110, cell: ({ getValue }) => formatDate(getValue() as string) },
    { id: 'receiptNumber', header: 'Voucher', accessorKey: 'receiptNumber', size: 160,
      cell: ({ row }) => (
        <span style={{ fontFamily: row.original.receiptNumber ? 'monospace' : undefined,
          color: row.original.receiptNumber ? undefined : 'var(--text-muted)' }}>{voucher(row.original)}</span>
      ) },
    { id: 'paymentMode', header: 'Mode', accessorKey: 'paymentMode', size: 120,
      cell: ({ row }) => (
        <span>{row.original.paymentMode}
          {row.original.paymentType === 'Refund' && <span className="badge badge-warning" style={{ fontSize: '0.62rem', marginLeft: '0.3rem' }}>Refund</span>}
        </span>
      ) },
    { id: 'paymentReference', header: 'Reference', accessorKey: 'paymentReference', size: 140, cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 130 },
    { id: 'guestFullName', header: 'Guest / Party', accessorKey: 'guestFullName',
      cell: ({ row }) => (
        <span>{row.original.guestFullName || '—'}
          {row.original.partyName && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{row.original.partyName}</div>}
        </span>
      ) },
    { id: 'roomNumbers', header: 'Rooms', accessorKey: 'roomNumbers', size: 90, cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'receivedByName', header: 'Recorded by', accessorKey: 'receivedByName', size: 140, cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'amount', header: 'Amount', accessorKey: 'amount', size: 120,
      cell: ({ getValue }) => {
        const v = getValue() as number;
        return <span style={{ color: v < 0 ? 'var(--danger, #dc2626)' : undefined }}>{formatCurrency(v)}</span>;
      } },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { refetch(); refetchSummary(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={16} />, label: exporting ? 'Exporting…' : 'Export', tooltip: 'Export to Excel', onClick: handleExport, variant: 'icon' });
      buttons.push({ id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Print', tooltip: 'Print collection report', onClick: handlePrint, variant: 'icon' });
    }
    return buttons;
  }, [refetch, refetchSummary, handleExport, handlePrint, exporting, printing, allowedActions]);

  const periodLabel = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Period', value: periodLabel, primary: true },
    { label: 'Bill point', value: 'Front Office', icon: 'tag' },
    ...(enabled && summary ? [
      { label: 'Receipts', value: `${formatCurrency(summary.receipts)}  (${summary.receiptCount})` },
      { label: 'Refunds', value: `${formatCurrency(summary.refunds)}  (${summary.refundCount})` },
      { label: 'Net collection', value: formatCurrency(summary.net) },
      ...(summary.pendingNumbers > 0 ? [{ label: 'Numbers pending', value: String(summary.pendingNumbers), icon: 'alert' as const,
        title: 'Numbered from Document Series once the property is linked to an entity' }] : []),
      ...(summary.recordedByAccounts > 0 ? [{ label: 'By Accounts', value: String(summary.recordedByAccounts), icon: 'user' as const,
        title: 'Recorded by Accounts in Fairbooks — their voucher' }] : []),
    ] : []),
  ];

  const footer = enabled && summary && summary.modes.length > 0 ? (
    <>
      <b>By mode</b>
      {summary.modes.map((m) => (
        <span key={m.paymentMode}>
          {m.paymentMode} ({m.count}) <b>{formatCurrency(m.net)}</b>
          {m.refunds > 0 && <span style={{ color: 'var(--text-muted)' }}> · refunds {formatCurrency(m.refunds)}</span>}
        </span>
      ))}
      <span style={{ marginLeft: 'auto' }}>Net <b>{formatCurrency(summary.net)}</b></span>
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Collection Report"
      description="Receipts and refunds by mode for a period — what the desk took in, with each voucher."
      icon={<IconSystem.creditCard size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (from !== todayIso() || to !== todayIso()) ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(todayIso()); setTo(todayIso()); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>
            Pick a property and a period in the filters (the period runs on the payment date).
          </div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error.message}</div>
        ) : (
          <SyutGrid
            data={data}
            columns={columns}
            enablePagination
            manualPagination
            totalCount={pagination?.totalItems ?? 0}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={(i) => setPage(i + 1)}
            onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
            isLoading={loading}
            emptyMessage="No receipts or refunds in the period."
          />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default CollectionReportScreen;
